import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { isEmpty } from 'lodash';
import { HOST_URL } from '../../../../environment/host-config/active';
import { SET_LOCAL_STREAMING_STATE, thunkFinishEvent } from '../../../../store/actions/Streaming.actions';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import useParticipants from '../../hooks/useParticipants/useParticipants';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

export default function useStreamingLogic() {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { getLocalVideoTrack, getLocalAudioTrack } = useVideoContext();

    const isCreatingLocalAudioTrackRef = useRef<boolean>(false);
    const isCreatingLocalVideoTrackRef = useRef<boolean>(false);

    const [openDisconnectModal, setOpenDisconnectModal] = useState<boolean>(false);

    const {
        user: { user_ide: userIde, user_username: localUserUsername },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);

    const {
        eventUuid,
        eventData: {
            user: { user_ide: influencerUserIde, user_username: influencerUsername },
        },
        streamingState: {
            // @ts-ignore
            current_state: { speaking_viewer: speakingViewer, open_mic: openMic },
        },
        streamingFlags: { hasTurn },
        localStreamingState: { room, camera, microphone },
        localStreamingState,
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const localParticipant = useMemo(() => {
        if (room) {
            return room.localParticipant;
        }
        return null;
    }, [room]);

    const participants = useParticipants();

    const mainParticipant = useMemo(() => {
        if (isEventInfluencer && localParticipant) {
            return localParticipant;
        }
        if (!isEventInfluencer) {
            return participants.find(participant => participant.identity === influencerUsername);
        }

        return null;
    }, [influencerUsername, isEventInfluencer, localParticipant, participants]);

    const remoteParticipant = useMemo(() => {
        if (!isEmpty(speakingViewer?.username)) {
            if (speakingViewer.username === localUserUsername) {
                return localParticipant;
            }

            return participants.find(participant => participant.identity === speakingViewer.username);
        }

        return null;
    }, [localParticipant, localUserUsername, participants, speakingViewer]);

    const disconnectEvent = () => {
        try {
            room?.disconnect();
        } catch (e) {}
        window.location.href = `${HOST_URL}/event/${eventUuid}`;
    };

    const closeEvent = () => {
        dispatch(thunkFinishEvent(() => disconnectEvent()));
    };

    const leaveEvent = () => {
        if (isEventInfluencer) {
            setOpenDisconnectModal(true);
        } else {
            disconnectEvent();
        }
    };

    const muteCamera = useCallback(
        (active?: boolean) => {
            if (room) {
                try {
                    room.localParticipant.videoTracks.forEach(track => {
                        track.track.enable(active);
                    });
                    dispatch({
                        type: SET_LOCAL_STREAMING_STATE,
                        payload: {
                            ...localStreamingState,
                            camera: active,
                        },
                    });
                } catch (error) {
                    console.error('Error onSetMicrophone:', error);
                }
            }
        },
        [dispatch, localStreamingState, room],
    );

    const muteMicrophone = useCallback(
        (active?: boolean) => {
            if (room) {
                try {
                    room.localParticipant.audioTracks.forEach(track => {
                        track.track.enable(active);
                    });
                    dispatch({
                        type: SET_LOCAL_STREAMING_STATE,
                        payload: {
                            ...localStreamingState,
                            microphone: active,
                        },
                    });
                } catch (error) {
                    console.error('Error onSetMicrophone:', error);
                }
            }
        },
        [dispatch, localStreamingState, room],
    );

    const createVideoTrack = useCallback(() => {
        if (!isCreatingLocalVideoTrackRef.current) {
            isCreatingLocalVideoTrackRef.current = true;

            getLocalVideoTrack()
                .then(localVideoTrack => {
                    if (localVideoTrack) {
                        return localParticipant?.publishTrack(localVideoTrack);
                    }
                    return null;
                })
                .then(() => {
                    if (!camera) {
                        muteCamera(false);
                    }
                })
                .catch(() => {
                    enqueueSnackbar(
                        "Unable to access your camera due to permissions with your browser. You won't be able to talk or show your video but you can still watch the event.",
                        { variant: 'error', autoHideDuration: 15000 },
                    );
                })
                .finally(() => {
                    isCreatingLocalVideoTrackRef.current = false;
                });
            /*
            createLocalVideoTrack()
                .then(localVideoTrack => {
                    return localParticipant?.publishTrack(localVideoTrack);
                })
                .then(publication => {
                    if (!camera) {
                        muteCamera(false);
                    }

                })
                .catch(e => {
                    enqueueSnackbar(
                        "Unable to access your camera due to permissions with your browser. You won't be able to talk or show your video but you can still watch the event.",
                        { variant: 'error', autoHideDuration: 15000 },
                    );
                })
                .finally(() => {
                    isCreatingLocalVideoTrackRef.current = false;
                });

             */
        }
    }, [camera, enqueueSnackbar, getLocalVideoTrack, localParticipant, muteCamera]);

    const createAudioTrack = useCallback(
        (ignoreLock?: boolean) => {
            if (!isCreatingLocalAudioTrackRef.current) {
                isCreatingLocalAudioTrackRef.current = true;
                // createLocalAudioTrack()

                getLocalAudioTrack(undefined, ignoreLock)
                    .then(localAudioTrack => {
                        if (localAudioTrack) {
                            return localParticipant?.publishTrack(localAudioTrack);
                        }
                        return null;
                    })
                    .then(() => {
                        if (!microphone) {
                            muteMicrophone(false);
                        }
                    })
                    .catch(() => {
                        enqueueSnackbar(
                            "Unable to access your microphone due to permissions with your browser. You won't be able to talk or show your video but you can still watch the event.",
                            { variant: 'error', autoHideDuration: 15000 },
                        );
                    })
                    .finally(() => {
                        isCreatingLocalAudioTrackRef.current = false;
                    });
            }
        },
        [enqueueSnackbar, getLocalAudioTrack, localParticipant, microphone, muteMicrophone],
    );

    useEffect(() => {
        if (!isEventInfluencer) {
            if (
                (hasTurn || openMic) &&
                isEmpty(localParticipant?.audioTracks) &&
                !isCreatingLocalAudioTrackRef.current
            ) {
                createAudioTrack();
            } else if (!hasTurn && !openMic && !isEmpty(localParticipant?.audioTracks)) {
                localParticipant?.audioTracks.forEach(track => localParticipant?.unpublishTrack(track.track));
            }

            if (hasTurn && isEmpty(localParticipant?.videoTracks) && !isCreatingLocalVideoTrackRef.current) {
                createVideoTrack();
            } else if (!hasTurn && !isEmpty(localParticipant?.videoTracks)) {
                localParticipant?.videoTracks.forEach(track => localParticipant?.unpublishTrack(track.track));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasTurn, isEventInfluencer, openMic]);

    return {
        openDisconnectModal,
        disconnectEvent,
        closeEvent,
        setOpenDisconnectModal,
        mainParticipant,
        leaveEvent,
        participants,
        influencerUsername,
        remoteParticipant,
        localUserUsername,
    };
}
